import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from "react";
import { Language, Translations } from "../types";
import { translationsApi } from "../services/api";

const emptyTranslations: Translations = {
  loading: "",
  greeting: "",
  introName: "",
  name: "",
  profession: "",
  navAbout: "",
  navSkills: "",
  navProjects: "",
  aboutTitle: "",
  stats: { experienceYears: "", technologies: "", projects: "" },
  cvButton: "",
  detailsBtn: "",
  cvLink: "",
  skillsTitle: "",
  frontEnd: "",
  backEnd: "",
  devopsTools: "",
  projectsTitle: "",
  projectsSubtitle: "",
  viewBtn: "",
  codeBtn: "",
  footerTitle: "",
  typingRoles: [],
};

const languageFromCountryCode = (countryCode?: string | null): Language =>
  countryCode?.toUpperCase() === "BR" ? "pt" : "en";

const languageFromNavigator = (): Language => {
  const navLang = navigator.language?.toLowerCase() ?? "";
  return navLang.startsWith("pt") ? "pt" : "en";
};

const detectLanguageByIp = async (): Promise<Language> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 4000);

  try {
    const response = await fetch("https://get.geojs.io/v1/ip/country.json", {
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error("Failed to resolve country by IP");
    }

    const data = (await response.json()) as { country?: string };
    return languageFromCountryCode(data.country);
  } catch {
    return languageFromNavigator();
  } finally {
    window.clearTimeout(timeoutId);
  }
};

interface LanguageContextType {
  lang: Language;
  t: Translations;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextType>({
  lang: "pt",
  t: emptyTranslations,
  toggleLanguage: () => {},
});

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>(() => languageFromNavigator());

  const [translations, setTranslations] = useState<Record<Language, Translations>>({
    pt: emptyTranslations,
    en: emptyTranslations,
  });

  useEffect(() => {
    let cancelled = false;

    void detectLanguageByIp().then((detected) => {
      if (cancelled) return;
      setLang(detected);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const response = await translationsApi.getAll();
        if (response.success && response.data) {
          const apiPt = (response.data.pt ?? {}) as Partial<Translations>;
          const apiEn = (response.data.en ?? {}) as Partial<Translations>;
          setTranslations({
            pt: {
              ...emptyTranslations,
              ...apiPt,
              stats: { ...emptyTranslations.stats, ...((apiPt as any).stats ?? {}) },
              typingRoles: Array.isArray((apiPt as any).typingRoles) ? (apiPt as any).typingRoles : emptyTranslations.typingRoles,
            } as Translations,
            en: {
              ...emptyTranslations,
              ...apiEn,
              stats: { ...emptyTranslations.stats, ...((apiEn as any).stats ?? {}) },
              typingRoles: Array.isArray((apiEn as any).typingRoles) ? (apiEn as any).typingRoles : emptyTranslations.typingRoles,
            } as Translations,
          });
        }
      } catch {
        // Keep empty defaults on error
      }
    };

    void loadTranslations();
  }, []);

  const toggleLanguage = useCallback(() => {
    setLang((prev) => (prev === "pt" ? "en" : "pt"));
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang === "pt" ? "pt-BR" : "en-US";
  }, [lang]);

  const value: LanguageContextType = {
    lang,
    t: translations[lang],
    toggleLanguage,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextType => useContext(LanguageContext);
